import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const sections = [
  {
    id: 1,
    title: "Ons onafhankelijkheidsbeginsel",
    content: (
      <div className="space-y-4">
        <p className="text-base font-medium text-foreground/90 leading-relaxed border-l-4 border-primary pl-4 italic">
          "Wij geven advies op basis van wat het beste is voor de huiseigenaar — niet op basis van wat hetmeest oplevert voor ons."
        </p>
        <p>
          Onafhankelijkheid is de hoeksteen van Verduurzaamwijzer.nu. Zonder onafhankelijkheid heeft ons advies
          geen waarde. Daarom hebben wij strikte regels opgesteld die onze redactionele vrijheid beschermen,
          ongeacht commerciële belangen.
        </p>
      </div>
    ),
  },
  {
    id: 2,
    title: "Hoe wij geld verdienen",
    content: (
      <div className="space-y-4">
        <p>
          Transparantie over onze financiering is essentieel. Verduurzaamwijzer.nu genereert inkomsten via de
          volgende kanalen:
        </p>
        <ul className="space-y-3 list-none">
          {[
            ["Leadvergoedingen", "Aanbieders betalen een vaste vergoeding per doorgestuurde offerte-aanvraag. Deze vergoeding is niet afhankelijk van de inhoud van ons advies."],
            ["Advertentieruimte", "Aanbieders kunnen betaalde vermeldingen afnemen. Deze zijn altijd duidelijk gemarkeerd als \'Gesponsord\' of \'Advertentie\'."],
            ["Abonnementen", "Aanbieders kunnen een abonnement nemen voor uitgebreide profielpagina\'s en extra zichtbaarheid."],
          ]?.map(([label, desc], i) => (
            <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              <span><strong>{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
          <strong>Belangrijk:</strong> Geen enkele aanbieder kan betalen voor een betere beoordeling, hogere
          positie in onze onafhankelijke vergelijkingen of positiever redactioneel advies. Onze
          beoordelingscriteria zijn vastgelegd en worden niet beïnvloed door commerciële relaties.
        </div>
      </div>
    ),
  },
  {
    id: 3,
    title: "Onze redactionele onafhankelijkheid",
    content: (
      <div className="space-y-4">
        <p>
          Onze redactie werkt volledig onafhankelijk van onze commerciële afdeling. Dit betekent concreet:
        </p>
        <ul className="space-y-2 list-none">
          {[
            "Redacteuren en adviseurs hebben geen kennis van commerciële afspraken met individuele aanbieders.",
            "Beoordelingen en vergelijkingen worden opgesteld op basis van objectieve criteria.",
            "Negatieve beoordelingen worden niet verwijderd op verzoek van aanbieders.",
            "Aanbieders die niet voldoen aan onze kwaliteitseisen worden van het platform verwijderd, ongeacht hun commerciële relatie.",
            "Redactionele content wordt niet beïnvloed door advertentie-inkomsten.",
          ]?.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 4,
    title: "Beoordelingscriteria aanbieders",
    content: (
      <div className="space-y-4">
        <p>
          Aanbieders worden beoordeeld op basis van de volgende objectieve criteria, ongeacht hun commerciële
          relatie met ons platform:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-muted/60">
                <th className="py-2 px-4 text-left font-semibold text-foreground/80 border border-border/40">Criterium</th>
                <th className="py-2 px-4 text-left font-semibold text-foreground/80 border border-border/40">Gewicht</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Certificeringen en kwalificaties", "25%"],
                ["Klanttevredenheid (reviews)", "25%"],
                ["Prijs-kwaliteitverhouding", "20%"],
                ["Garantievoorwaarden", "15%"],
                ["Servicekwaliteit en nazorg", "15%"],
              ]?.map(([label, weight], i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-muted/40" : ""}>
                  <td className="py-2 px-4 text-foreground/80 border border-border/40">{label}</td>
                  <td className="py-2 px-4 text-foreground/70 border border-border/40 font-medium">{weight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  {
    id: 5,
    title: "Klachten en meldingen",
    content: (
      <div className="space-y-4">
        <p>
          Vermoedt u dat onze onafhankelijkheid in het geding is, of heeft u een klacht over een aanbieder op
          ons platform? Wij nemen dit uiterst serieus.
        </p>
        <p>
          Meld uw bezwaar via{' '}
          <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline font-medium">
            contact@verduurzaamwijzer.nu
          </a>{' '}
          met als onderwerp "Onafhankelijkheid". Wij reageren binnen 5 werkdagen.
        </p>
        <p>
          Meer informatie over onze werkwijze vindt u op de{' '}
          <Link to="/werkwijze" className="text-primary hover:underline font-medium">Werkwijze-pagina</Link>{' '}
          en in ons{' '}
          <Link to="/transparantie" className="text-primary hover:underline font-medium">Transparantierapport</Link>.
        </p>
      </div>
    ),
  },
];

const OnafhankelijkheidPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/8 via-background to-background pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-foreground/50 mb-4">
              <Link to="/homepage" className="hover:text-primary transition-colors">Home</Link>
              <Icon name="ChevronRight" size={14} />
              <Link to="/over-ons" className="hover:text-primary transition-colors">Over Ons</Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-foreground/70">Onafhankelijkheid</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="ShieldCheck" size={20} className="text-primary" />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">Onafhankelijkheid</h1>
            </div>
            <p className="text-foreground/70 text-lg leading-relaxed max-w-2xl">
              Hoe wij onze onafhankelijkheid waarborgen en waarom dat de basis is van alles wat wij doen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Inhoud</h2>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#section-${s?.id}`}
                      className="block text-sm text-foreground/60 hover:text-primary transition-colors py-0.5 leading-snug"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <main className="flex-1 min-w-0">
              <div className="space-y-10">
                {sections?.map((section, index) => (
                  <motion.div
                    key={section?.id}
                    id={`section-${section?.id}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.4, delay: index * 0.03 }}
                    className="scroll-mt-24"
                  >
                    <h2 className="font-heading text-lg font-semibold text-foreground mb-3 pb-2 border-b border-border/50">
                      {section?.title}
                    </h2>
                    <div className="text-foreground/75 leading-relaxed text-sm sm:text-base">
                      {section?.content}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="mt-12 bg-primary/5 border border-primary/20 rounded-xl p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="ShieldCheck" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">Vragen over onze onafhankelijkheid?</h3>
                    <p className="text-sm text-foreground/70 mb-3">
                      Neem contact op via{' '}
                      <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline font-medium">
                        contact@verduurzaamwijzer.nu
                      </a>{' '}
                      of bel{' '}
                      <a href="tel:0647650537" className="text-primary hover:underline font-medium">
                        06 4765 0537
                      </a>.
                    </p>
                    <p className="text-xs text-foreground/50">
                      De Content Factory BV · Oude Middenweg 17, 2491 AC Den Haag · KvK 84512490
                    </p>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OnafhankelijkheidPage;
